import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import {
  IProductionDashboardBay,
  IProductionDashboardTimelineEntry,
} from "../../models/ProductionDashboard";
import {
  ITimelineBlock,
  nowPercent,
  timelineBlocks,
  timelineTicks,
  timelineWindow,
} from "../../utils/bayTimelineLayout";
import classNames from "../../utils/classNames";

interface BayTimelineProps {
  bays: IProductionDashboardBay[];
  entries: IProductionDashboardTimelineEntry[];
  now: DateTime;
}

const LABEL_WIDTH = "w-28";

// The bar is a pale wash and each station spell a solid fill inside it, so time between two
// spells of the same vehicle reads as a gap rather than work.
const blockTone = (block: ITimelineBlock) =>
  block.isRunning
    ? { wash: "bg-emerald-500/20", fill: "bg-emerald-500 text-white" }
    : block.isComplete
      ? { wash: "bg-blue-200/50", fill: "bg-blue-200 text-blue-900" }
      : { wash: "bg-gray-300/50", fill: "bg-gray-300 text-gray-800" };

// Who was on which bay when, across today, from the vehicle station history. One row per bay; a
// gap is time the bay stood empty; a bar splits where the vehicle moved station.
export default function BayTimeline({ bays, entries, now }: BayTimelineProps) {
  const { t } = useTranslation("production");
  const window = timelineWindow(entries, now);
  const ticks = timelineTicks(window);
  const nowLeft = nowPercent(window, now);

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4 flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
            {t("Bay Timeline Today")}
          </h2>
          <span className="text-[11px] text-gray-500">
            {t("Gaps are time a bay stood empty; hover a bar for the station")}
          </span>
        </div>
        <div className="flex gap-3 text-[11px] text-gray-600">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm bg-blue-200" />
            {t("Completed")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm bg-emerald-500" />
            {t("On bay now")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm bg-gray-300" />
            {t("Stopped part-way")}
          </span>
        </div>
      </div>

      <div className="relative flex flex-col gap-1.5">
        {/* Hour ticks */}
        <div className="flex">
          <div className={LABEL_WIDTH} />
          <div className="relative flex-1 h-4">
            {ticks.map((tick) => (
              <span
                key={tick.label}
                className="absolute top-0 -translate-x-1/2 text-[10px] text-gray-500 tabular-nums"
                style={{ left: `${tick.leftPercent}%` }}
              >
                {tick.label}
              </span>
            ))}
          </div>
        </div>

        {bays.map((bay) => {
          const blocks = timelineBlocks(
            entries.filter((e) => e.dismantlingBayId === bay.id),
            window,
            now
          );
          return (
            <div key={bay.id} className="flex items-center h-8">
              <div className={classNames(LABEL_WIDTH, "text-xs font-semibold text-gray-700 truncate pr-2")}>
                {bay.name}
              </div>
              <div className="relative flex-1 h-6 rounded bg-gray-50">
                {blocks.map((block) => {
                  const tone = blockTone(block);
                  return (
                    <div
                      key={block.key}
                      className={classNames("absolute top-0 h-6 rounded overflow-hidden", tone.wash)}
                      style={{ left: `${block.leftPercent}%`, width: `${block.widthPercent}%` }}
                    >
                      {block.segments.map((segment, i) => (
                        <div
                          key={`${segment.partAssemblyId}-${segment.startLabel}`}
                          className={classNames(
                            "absolute top-0 h-6",
                            tone.fill,
                            i > 0 && "border-l border-white/70",
                            segment.isManual && "border-b-2 border-b-amber-400"
                          )}
                          style={{ left: `${segment.leftPercent}%`, width: `${segment.widthPercent}%` }}
                          title={[
                            block.vrm || t("No VRM"),
                            segment.name ?? t("Unknown station"),
                            segment.endLabel
                              ? `${segment.startLabel}–${segment.endLabel}`
                              : t("{{start}} – now", { start: segment.startLabel }),
                            segment.isManual ? t("moved by hand") : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        />
                      ))}
                      <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold whitespace-nowrap text-gray-900 mix-blend-multiply">
                        {block.vrm || t("No VRM")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Now marker, drawn over the track column only */}
        <div className="absolute inset-y-0 left-28 right-0 pointer-events-none">
          <div
            className="absolute top-4 bottom-0 w-0.5 bg-gray-900"
            style={{ left: `${nowLeft}%` }}
          />
          <span
            className="absolute top-0 -translate-x-1/2 rounded bg-gray-900 px-1 text-[10px] font-bold text-white tabular-nums"
            style={{ left: `${nowLeft}%` }}
          >
            {now.toFormat("HH:mm")}
          </span>
        </div>
      </div>
    </div>
  );
}
